// @flow

import $ from 'jquery';
import pick from 'lodash/pick';
import React from 'react';
import styled from 'styled-components';
import mixpanel from 'mixpanel-browser';
import SkippableChallenge from './SkippableChallenge';
import isSamsungBuiltinBrowser from '../../../lib/isSamsungBuiltinBrowser';
import isPageLoadedInsideIframe from '../../../lib/isPageLoadedInsideIframe';
import { Dots } from 'react-activity';

import './SpotXChallenge.css';



// Adapted from the tag generator on https://developer.spotxchange.com/content/local/docs/sdkDocs/EASI/README.md


function isMobileOrTablet() {
    let check = false;
    (function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(a.substr(0,4)))check = true})(navigator.userAgent||navigator.vendor||window.opera);
    return check;
}


function appendSpotXScriptTag(props, channelId: string, width: number, height: number) {
    const attrs = [`type="text/javascript"
        src="https://js.spotx.tv/easi/v1/${channelId}.js"
        data-spotx_ad_unit="incontent"
        data-spotx_ad_done_function="spotXFinishedPlaying"
        data-spotx_content_container_id="spotxvideo${videoId}"
        data-spotx_autoplay="1"
        data-spotx_ad_format="html5"
        data-spotx_loop="0"
        data-spotx_https="1"`
    ];
    if (isMobileOrTablet()) {
        attrs.push('data-spotx_ad_volume="0"');
        // attrs.push('data-spotx_click_to_replay="1"');
    } else {
        attrs.push('data-spotx_unmute_on_mouse="0"');
        attrs.push('data-spotx_ad_volume="70"');
    }
    if (isPageLoadedInsideIframe()) {
        attrs.push(`data-spotx_content_width="${width}"`);
        attrs.push(`data-spotx_content_height="${height}"`);
    }

    attrs.push(`data-spotx_channel_id="${channelId}"`);

    const paramNames = [
        'device_os',
        'device_osv',
        'app_storeurl',
        'device_ifa',
        'app_bundle',
        'app_name',
        'app_domain',
    ];

    const queryParams = props.params.queryParams;
    const idfa = queryParams.iOSIdFA || queryParams.AndroidIDFA || queryParams.device_ifa || (queryParams.distinctIdFallback === '1' ? mixpanel.get_distinct_id() : null);
    if (idfa) {
        attrs.push(`data-spotx_device_ifa="${idfa}"`);
    }

    paramNames.forEach(function (paramName) {
        let value = props.params.queryParams[paramName];
        if (paramName === 'app_domain' && props.publisherAppDomainName) {
            // App domain can be overriden by setting it in the publisher app database.
            // In this case, we get the app domain name to send from the backend in the ad response.
            value = props.publisherAppDomainName;
        }
        if (value) {
            attrs.push(`data-spotx_${paramName}="${value.replace(/"/, '&quot;')}"`);
        }
    });

    $(`<script ${attrs.join(' ')}></script>`).appendTo('head');
}


let videoId = 0;


const BlackVideoBackground = styled.div`
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at center, #636363, black 100%);

    #spot-x-video {
        width: 100% !important;
        height: 100% !important;
    }
`;


class SpotXChallenge extends SkippableChallenge {
    handleResize: ?(() => void);
    waitForShownInterval: number;
    resizeInterval: number;

    handleResize = () => {
        const sizingStyle = this.sizingStyle(window.innerWidth, window.innerHeight);
        $(`#spotxvideo${videoId}`).css(sizingStyle);
        const sizeAttrs = { width: parseInt(sizingStyle.width, 10), height: parseInt(sizingStyle.height, 10) };
        $(`#spotx-container${videoId} iframe`).attr(sizeAttrs);
        const iframe = $('.spotx_surrogate_iframe')[0];
        if (iframe) {
            setTimeout(() => {
                const videoCanvasTag = iframe.contentDocument.getElementsByTagName('canvas')[0];
                if (!iframe || !iframe.contentDocument || !videoCanvasTag) return;
                if (videoCanvasTag) {
                    $(videoCanvasTag).css(pick(sizingStyle, 'width', 'height'));
                }
            }, 100);
        }
    };

    static disablesSupportButton() {
        return true;
    }

    static isSupportedOnCurrentPlatform() {
        return !isSamsungBuiltinBrowser();
    }

    componentDidMount() {
        super.componentDidMount();
        this.eventReady();

        videoId += 1;

        // This is necessary to ensure the video is not appended + autostarted before the ad is
        // actually visible (which might be the case directly after an SDK preloads the ad).
        this.waitForShownInterval = setInterval(() => {
            const height = window.innerHeight;
            if (height !== 0) {
                this.appendSpotXTags();
                clearInterval(this.waitForShownInterval);
            }
        }, 10);
    }

    addWindowResizeHandler() {
        this.handleResize();
        window.addEventListener('resize', this.handleResize);
        window.addEventListener('orientationChange', this.handleResize);
    }

    componentWillMount() {
        window.spotXFinishedPlaying = (adFound) => {
            setTimeout(() => {
                this.handleSpotXFinishing(adFound);
            }, 50);
        };
    }

    componentWillUnmount() {
        super.componentWillUnmount();
        window.removeEventListener('resize', this.handleResize);
        window.removeEventListener('orientationchange', this.handleResize);
        $(`#spotxvideo${videoId}`).css({ opacity: 0, pointerEvents: 'none', left: '-10000px', zIndex: -1000 });
        // Don't remove the iframe, this reopens the fullscreen video view on iOS due to a bug.
        $('#spotx-javascript').remove();
        delete window.spotXFinishedPlaying;
        clearInterval(this.waitForShownInterval);
        clearInterval(this.resizeInterval);
    }


    render() {
        return <BlackVideoBackground className={`${this.props.className || ''} challengeContainer`}>
            <div className="loading"><Dots size={30} /></div>
        </BlackVideoBackground>;
    }



    appendSpotXTags() {
        $(`<div id='spotx-container${videoId}' class='spot-x-video-iframe-outer-container'></div>`).appendTo('body');
        $(`<div id='spotxvideo${videoId}' class='spot-x-video-iframe-container'></div>`).appendTo(`#spotx-container${videoId}`);
        this.addWindowResizeHandler();
        setTimeout(() => {
            const size = this.sizingStyle(window.innerWidth, window.innerHeight);
            const channelId = this.props.challenge.placement;
            if (typeof channelId === 'string') {
                appendSpotXScriptTag(this.props, channelId, parseInt(size.width, 10), parseInt(size.height, 10));
            }
            setTimeout(() => { window.dispatchEvent(new Event('resize')); }, 100)
        }, 100);
    }


    handleSpotXFinishing(adFound: boolean) {
        this.trackAdOpportunity(adFound);

        $(`#spotx-container${videoId}`).remove();

        if (adFound)  {
            this.trackImpression();
            this.eventComplete();
        }
        else {
            this.eventPassback();
        }
    }


    static shouldShowSkipButtonOnStart() {
        return false;
    }


    sizingStyle(windowWidth: number, windowHeight: number) {
        const width = Math.round(Math.min(windowWidth, windowHeight * 16 / 9));
        const height = Math.round((9 / 16) * width);
        const top = Math.round(0.5 * (windowHeight - height));
        const left = Math.round(0.5 * (windowWidth - width));
        return { position: 'absolute', width: `${width}px`, height: `${height}px`, top: `${top}px`, left: `${left}px` };
    }
}

export default SpotXChallenge;



// WEBPACK FOOTER //
// ./src/campaign/challenges/external-sdks/SpotXChallenge.jsx